import { useCallback, useEffect, useState } from "react";
import { fetchRunStatus } from "../api/client";
import type { RunReport } from "../api/types";

const POLL_INTERVAL_MS = 4000;

export interface UseRunStatusResult {
  report: RunReport | null;
  loading: boolean;
  error: Error | null;
  reload: () => void;
}

/**
 * Tracks the current indexing run, polling `GET /api/admin/runs` every few seconds while one is
 * `running` and stopping otherwise. Lifted to `AppShell` (one instance for the whole app) rather
 * than owned by `RunsScreen` alone, so the "indexing in progress" nav indicator stays live no
 * matter which screen is open, and so navigating away and back doesn't restart a second poll loop.
 *
 * @param enabled False for a caller who is not an administrator — the route would refuse them, and
 *   polling a 403 every four seconds is noise in their browser and in the server's log alike.
 */
export function useRunStatus(enabled = true): UseRunStatusResult {
  const [report, setReport] = useState<RunReport | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(() => {
    if (!enabled) {
      return;
    }
    fetchRunStatus()
      .then((result) => {
        setReport(result);
        setError(null);
      })
      .catch((caught: unknown) => {
        setError(caught instanceof Error ? caught : new Error(String(caught)));
      })
      .finally(() => setLoading(false));
  }, [enabled]);

  useEffect(() => {
    load();
  }, [load]);

  const isRunning = report?.status.state === "running";

  useEffect(() => {
    if (!isRunning) {
      return undefined;
    }
    const interval = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isRunning, load]);

  return { report, loading, error, reload: load };
}
